import { useState } from "react";
import { toast } from "sonner";
import { GoldDivider, Eyebrow, EditorialHeading } from "../../components/brand";
import { api, formatApiError } from "../../lib/api";
import { track } from "../../lib/analytics";
import { LOGO, BUSINESS_INFO } from "../../content/site";
import {
  DollarSign, Users, Globe2, TrendingUp, ShieldCheck, ArrowUpRight,
  CheckCircle2, Sparkles, Handshake,
} from "lucide-react";

const REGION_OPTIONS = ["USA", "Caribbean", "Europe", "Africa", "Asia", "None"];
const SHARING_OPTIONS = ["Social Media", "Email Marketing", "Personal Introductions / Networking", "Website / Blog", "Other"];
const NETWORK_OPTIONS = ["0–50", "51–200", "201–500", "500+"];
const PAYOUT_OPTIONS = ["Bank Transfer", "PayPal", "Zelle", "Cash App", "Other"];

export default function GlobalLinkApply() {
  return (
    <div data-testid="page-global-link">
      <HeroSection />
      <ProgramNarrative />
      <HowItWorks />
      <WhatYouGet />
      <WhyJoin />
      <ApplicationSection />
    </div>
  );
}

function HeroSection() {
  return (
    <section className="bg-altura-espresso text-altura-ivory pt-24 pb-32 relative noise overflow-hidden">
      {/* Decorative gold arc */}
      <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full border border-altura-gold/20 opacity-70" />
      <div className="pointer-events-none absolute -right-24 top-10 h-[320px] w-[320px] rounded-full border border-altura-gold/10" />
      <div className="container-editorial relative z-10 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-3">
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_invest-beyond-2/artifacts/ru517vq0_G%20LOGO%20JPEG.jpg"
            alt="The Global Link — Real Estate Affiliate"
            className="w-full max-w-[240px] rounded-full drop-shadow-2xl bg-altura-ivory p-2"
            data-testid="global-link-logo"
          />
        </div>
        <div className="lg:col-span-9">
          <Eyebrow className="text-altura-gold">The Global Link · TGL Affiliate Program</Eyebrow>
          <EditorialHeading size="lg" className="mt-4 text-altura-ivory">
            Turn your network into income.
          </EditorialHeading>
          <p className="mt-5 text-altura-ivory/75 max-w-2xl leading-relaxed">
            <strong className="text-altura-ivory">TGL — The Global Link Program</strong> is Altura Global Real Estate's official referral and affiliate partner program. Perfect for entrepreneurs, professionals, and global connectors who want to monetize their network by introducing clients, investors, and buyers to premium real estate opportunities worldwide.
          </p>
          <GoldDivider className="mt-6" />
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-altura-gold/50 bg-altura-gold/10 px-5 py-2.5 text-sm text-altura-gold" data-testid="global-link-commission">
              <DollarSign className="h-4 w-4" />
              Earn <strong className="mx-1 text-altura-gold-light">{BUSINESS_INFO.global_link_commission}</strong>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-altura-ivory/20 px-5 py-2.5 text-sm text-altura-ivory/80">
              <Globe2 className="h-4 w-4 text-altura-gold" />
              Dubai · Kenya · Ghana · Punta Cana · Tanzania · Zanzibar
            </div>
          </div>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a href="#apply" className="btn-gold" data-testid="global-link-apply-cta">
              I'm ready to join TGL <ArrowUpRight className="h-4 w-4" />
            </a>
            <a href="#how-it-works" className="btn-outline-gold border-altura-ivory/70 text-altura-ivory hover:bg-altura-ivory/10">
              See how it works
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProgramNarrative() {
  return (
    <section className="bg-altura-ivory py-20 border-t border-altura-espresso/5">
      <div className="container-editorial grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-5">
          <Eyebrow>Who it's for</Eyebrow>
          <EditorialHeading size="md" className="mt-4">
            Built for connectors, professionals & global networks.
          </EditorialHeading>
          <GoldDivider className="mt-6" />
        </div>
        <div className="lg:col-span-7 space-y-6 text-altura-charcoal/80 leading-relaxed">
          <p>
            Whether you're a professional in <strong>finance, travel, hospitality</strong>, or you simply have an international network, <em>The Global Link Program</em> gives you the tools, resources, and support to monetize your connections while introducing them to premium real estate opportunities worldwide.
          </p>
          <p className="text-sm text-altura-charcoal/60">
            We serve: <strong className="text-altura-espresso">Dubai, Kenya, Ghana, Punta Cana, Tanzania, Zanzibar</strong>, and more.
          </p>
        </div>
      </div>
    </section>
  );
}

function StepCard({ icon: Icon, title, body, index }) {
  return (
    <div className="relative rounded-lg border border-altura-espresso/10 bg-white p-8 transition-all hover:border-altura-gold/50 hover:shadow-[0_20px_60px_-15px_rgba(58,31,20,0.15)]">
      <div className="absolute -top-3 left-6 inline-flex items-center gap-2 rounded-full bg-altura-espresso px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-altura-gold">
        Step {index}
      </div>
      <Icon className="h-6 w-6 text-altura-gold-dark" />
      <h3 className="mt-4 font-editorial text-xl text-altura-espresso leading-tight">{title}</h3>
      <p className="mt-2 text-sm text-altura-charcoal/70 leading-relaxed">{body}</p>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    { icon: Handshake, title: "Register once", body: "Complete a one-time registration and get personally reviewed by the Altura Global team." },
    { icon: Sparkles, title: "Receive your kit", body: "Get your unique referral link, access to your Affiliate Dashboard, and a curated welcome kit." },
    { icon: Users, title: "Share opportunities", body: "Introduce Altura Global to clients, investors, and buyers in your network — anywhere in the world." },
    { icon: DollarSign, title: "Earn on every close", body: "Earn a flat $250 commission for every successful referral. Paid 30 days after a closed sale." },
  ];
  return (
    <section id="how-it-works" className="bg-altura-beige/30 py-24">
      <div className="container-editorial">
        <div className="max-w-3xl">
          <Eyebrow>How it works</Eyebrow>
          <EditorialHeading size="md" className="mt-4">Simple. Transparent. Rewarding.</EditorialHeading>
          <GoldDivider className="mt-6" />
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => <StepCard key={s.title} {...s} index={i + 1} />)}
        </div>
      </div>
    </section>
  );
}

function WhatYouGet() {
  const items = [
    "Affiliate Welcome Kit with step-by-step instructions",
    "Access to tracking tools and your Affiliate Dashboard",
    "Ready-to-use marketing materials: banners, email templates, referral scripts",
    "Ongoing support from Altura Global Real Estate",
  ];
  return (
    <section className="bg-altura-ivory py-24">
      <div className="container-editorial grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-5">
          <Eyebrow>What you get</Eyebrow>
          <EditorialHeading size="md" className="mt-4">Everything you need to convert your network into commissions.</EditorialHeading>
          <GoldDivider className="mt-6" />
          <p className="mt-6 text-altura-charcoal/70 leading-relaxed">
            When you're approved as a TGL partner, we equip you with the assets, tools, and personal support to represent Altura Global with confidence.
          </p>
        </div>
        <ul className="lg:col-span-7 space-y-4">
          {items.map((it) => (
            <li key={it} className="flex items-start gap-4 rounded-lg border border-altura-espresso/10 bg-white p-5">
              <CheckCircle2 className="h-5 w-5 text-altura-gold-dark mt-0.5 shrink-0" />
              <span className="text-altura-charcoal/85 leading-relaxed">{it}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function WhyJoin() {
  const points = [
    { icon: DollarSign, title: "Flat $250 per referral", body: "Simple, predictable, and above-market commission on every closed sale." },
    { icon: Globe2, title: "Global reach", body: "Premium properties across Ghana, Dubai, Kenya, Punta Cana, Tanzania & Zanzibar." },
    { icon: TrendingUp, title: "Real dashboard, real tracking", body: "See referrals, statuses, and commissions in a purpose-built affiliate dashboard." },
    { icon: ShieldCheck, title: "Paid on time. Every time.", body: "Payouts issued 30 days after close. Transparent, direct, and reliable." },
  ];
  return (
    <section className="bg-altura-espresso text-altura-ivory py-24 relative noise">
      <div className="container-editorial">
        <div className="max-w-3xl">
          <Eyebrow className="text-altura-gold">Why join TGL</Eyebrow>
          <EditorialHeading size="md" className="mt-4 text-altura-ivory">A premium program built for serious connectors.</EditorialHeading>
          <GoldDivider className="mt-6" />
        </div>
        <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {points.map((p) => (
            <div key={p.title} className="rounded-lg border border-altura-ivory/10 bg-white/5 p-6 backdrop-blur">
              <p.icon className="h-6 w-6 text-altura-gold" />
              <h3 className="mt-4 font-editorial text-lg text-altura-ivory leading-tight">{p.title}</h3>
              <p className="mt-2 text-sm text-altura-ivory/70 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 rounded-lg border border-altura-gold/30 bg-altura-gold/5 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-altura-gold-light">Important note</div>
          <p className="mt-3 text-sm text-altura-ivory/80 leading-relaxed">
            Please ensure your payout information is accurate. Altura Global Real Estate is <strong className="text-altura-ivory">not responsible for failed payments</strong> if incorrect account details, emails, or phone numbers are provided.
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------
// Application form — captures the full TGL questionnaire.
// ---------------------------------------------------------------

const initialForm = {
  name: "", email: "", phone_country_code: "US +1", phone: "", message: "",
  residence: "", profession: "", preferred_contact: "",
  social_media: "",
  in_related_industry: "",
  network_size: "",
  regions: [],
  sharing_methods: [],
  prior_affiliate: "",
  why_join: "",
  time_commitment: "",
  payout_methods: [],
  understands_commission: "",
  agrees_to_tc: "",
  consent: false,
};

function ApplicationSection() {
  const [f, setF] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(null);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => setF((p) => ({
    ...p,
    [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/global-link-applications", {
        name: f.name,
        email: f.email,
        phone: f.phone,
        phone_country_code: f.phone_country_code,
        residence: f.residence,
        profession: f.profession,
        preferred_contact: f.preferred_contact,
        social_media: f.social_media,
        in_related_industry: f.in_related_industry,
        network_size: f.network_size,
        regions: f.regions,
        sharing_methods: f.sharing_methods,
        prior_affiliate: f.prior_affiliate,
        why_join: f.why_join,
        time_commitment: f.time_commitment,
        payout_methods: f.payout_methods,
        understands_commission: f.understands_commission,
        agrees_to_tc: f.agrees_to_tc,
        message: f.message,
        consent: f.consent,
      });
      setOk(data);
      toast.success("Application received. Welcome to The Global Link.");
      track.globalLinkApply();
      window.scrollTo({ top: document.getElementById("apply").offsetTop, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = "w-full rounded-md border border-altura-espresso/15 bg-white px-3.5 py-2.5 text-sm text-altura-charcoal placeholder:text-altura-charcoal/40 focus:border-altura-gold focus:outline-none focus:ring-1 focus:ring-altura-gold/50";
  const label = "text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60";

  if (ok) {
    return (
      <section id="apply" className="bg-altura-ivory py-24">
        <div className="container-editorial max-w-2xl">
          <div className="rounded-lg border border-altura-gold/30 bg-altura-gold/5 p-10 text-center" data-testid="global-link-success">
            <div className="eyebrow text-altura-gold-dark mx-auto">Welcome to TGL</div>
            <h3 className="mt-4 font-editorial text-3xl text-altura-espresso">Your application is with us.</h3>
            <p className="mt-4 text-altura-charcoal/70 leading-relaxed">
              An Altura Global advisor will personally review your application and reach out within 1–2 business days with next steps, your Affiliate Welcome Kit, and access to your Affiliate Dashboard.
            </p>
            <p className="mt-3 text-xs text-altura-charcoal/50">Reference: {ok.referral_partner_id?.slice(-8) || ok.lead_id?.slice(-8)}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="apply" className="bg-altura-ivory py-24 border-t border-altura-espresso/5">
      <div className="container-editorial grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-4">
          <Eyebrow>Apply now</Eyebrow>
          <EditorialHeading size="md" className="mt-4">I'm ready to join TGL.</EditorialHeading>
          <GoldDivider className="mt-6" />
          <p className="mt-6 text-altura-charcoal/75 leading-relaxed">
            Applications are reviewed personally by the Altura Global team. Approved partners receive a dedicated onboarding call, referral tools, and access to a curated opportunity library.
          </p>
          <p className="mt-6 text-sm text-altura-charcoal/60">
            <strong className="text-altura-espresso">Start earning today</strong> by connecting your network to global real estate opportunities.
          </p>
        </div>
        <div className="lg:col-span-8">
          <div className="rounded-lg border border-altura-espresso/15 bg-white p-8 lg:p-10">
            <form onSubmit={submit} className="space-y-6" data-testid="global-link-form">
              {/* Personal */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={label}>Full name *</label>
                  <input required className={field} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="gl-name" />
                </div>
                <div>
                  <label className={label}>Email *</label>
                  <input type="email" required className={field} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="gl-email" />
                </div>
              </div>

              <div>
                <label className={label}>Phone</label>
                <div className="mt-1 grid grid-cols-[110px_1fr] gap-2">
                  <input className={field} value={f.phone_country_code} onChange={(e) => set("phone_country_code", e.target.value)} placeholder="US +1" data-testid="gl-phone-cc" />
                  <input className={field} value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="9172433061" data-testid="gl-phone" />
                </div>
              </div>

              <div>
                <label className={label}>State / City / Country of Residence</label>
                <input className={field} value={f.residence} onChange={(e) => set("residence", e.target.value)} data-testid="gl-residence" />
              </div>

              <div>
                <label className={label}>Current profession / job title</label>
                <input className={field} value={f.profession} onChange={(e) => set("profession", e.target.value)} data-testid="gl-profession" />
              </div>

              <div>
                <label className={label}>Preferred method of communication</label>
                <select className={field} value={f.preferred_contact} onChange={(e) => set("preferred_contact", e.target.value)} data-testid="gl-preferred-contact">
                  <option value="">Select…</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className={label}>Instagram / Facebook / TikTok / Website</label>
                <textarea rows={2} className={field} value={f.social_media} onChange={(e) => set("social_media", e.target.value)} placeholder="Share your handles and/or website" data-testid="gl-social" />
              </div>

              <div>
                <label className={label}>Do you currently work in real estate, finance, investment, travel, or a related industry?</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {["yes", "no"].map((v) => (
                    <label key={v} className={`radio-tile ${f.in_related_industry === v ? "radio-tile-active" : ""}`}>
                      <input type="radio" name="in_related_industry" value={v} checked={f.in_related_industry === v} onChange={() => set("in_related_industry", v)} data-testid={`gl-industry-${v}`} />
                      <span>{v.toUpperCase()}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Network size */}
              <div>
                <label className={label}>How large is your professional or personal network?</label>
                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                  {NETWORK_OPTIONS.map((v) => (
                    <label key={v} className={`radio-tile ${f.network_size === v ? "radio-tile-active" : ""}`}>
                      <input type="radio" name="network_size" value={v} checked={f.network_size === v} onChange={() => set("network_size", v)} data-testid={`gl-network-${v}`} />
                      <span>{v}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Regions */}
              <div>
                <label className={label}>What regions do you have connections in? (Select all that apply)</label>
                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                  {REGION_OPTIONS.map((v) => (
                    <label key={v} className={`radio-tile ${f.regions.includes(v) ? "radio-tile-active" : ""}`}>
                      <input type="checkbox" checked={f.regions.includes(v)} onChange={() => toggle("regions", v)} data-testid={`gl-region-${v}`} />
                      <span>{v}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sharing methods */}
              <div>
                <label className={label}>How do you plan to share referral opportunities? (Select all that apply)</label>
                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                  {SHARING_OPTIONS.map((v) => (
                    <label key={v} className={`radio-tile ${f.sharing_methods.includes(v) ? "radio-tile-active" : ""}`}>
                      <input type="checkbox" checked={f.sharing_methods.includes(v)} onChange={() => toggle("sharing_methods", v)} data-testid={`gl-sharing-${v.replace(/[\s/]+/g, '-').toLowerCase()}`} />
                      <span>{v}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Prior affiliate */}
              <div>
                <label className={label}>Have you previously participated in any affiliate or referral programs?</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {["yes", "no"].map((v) => (
                    <label key={v} className={`radio-tile ${f.prior_affiliate === v ? "radio-tile-active" : ""}`}>
                      <input type="radio" name="prior_affiliate" value={v} checked={f.prior_affiliate === v} onChange={() => set("prior_affiliate", v)} data-testid={`gl-prior-${v}`} />
                      <span>{v.toUpperCase()}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className={label}>Why do you want to join The Global Link Program?</label>
                <textarea rows={4} className={field} value={f.why_join} onChange={(e) => set("why_join", e.target.value)} data-testid="gl-why" />
              </div>

              <div>
                <label className={label}>How much time per week can you dedicate to referring clients?</label>
                <textarea rows={2} className={field} value={f.time_commitment} onChange={(e) => set("time_commitment", e.target.value)} placeholder="e.g. 3–5 hours per week" data-testid="gl-time-commitment" />
              </div>

              {/* Preferred Payout Method */}
              <div>
                <label className={label}>Preferred payout method (Bank Transfer / PayPal / Zelle / Cash App / Other)</label>
                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                  {PAYOUT_OPTIONS.map((v) => (
                    <label key={v} className={`radio-tile ${f.payout_methods.includes(v) ? "radio-tile-active" : ""}`}>
                      <input type="checkbox" checked={f.payout_methods.includes(v)} onChange={() => toggle("payout_methods", v)} data-testid={`gl-payout-${v.replace(/\s+/g, '-').toLowerCase()}`} />
                      <span>{v}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Commission understanding */}
              <div className="rounded-md border border-altura-espresso/10 bg-altura-beige/20 p-5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-altura-espresso font-semibold">Terms & Conditions — Commission</div>
                <p className="mt-2 text-sm text-altura-charcoal/75 leading-relaxed">
                  Do you understand that you will earn a flat <strong className="text-altura-espresso">$250 commission per successful referral, paid 30 days after the closed sale?</strong>
                </p>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {[
                    { v: "yes", label: "Yes, I understand & agree" },
                    { v: "no", label: "No" },
                  ].map((o) => (
                    <label key={o.v} className={`radio-tile ${f.understands_commission === o.v ? "radio-tile-active" : ""}`}>
                      <input type="radio" name="understands_commission" value={o.v} checked={f.understands_commission === o.v} onChange={() => set("understands_commission", o.v)} data-testid={`gl-commission-${o.v}`} />
                      <span>{o.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* T&C abide */}
              <div>
                <label className={label}>Do you agree to abide by the TGL Affiliate Program Terms & Conditions?</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {["yes", "no"].map((v) => (
                    <label key={v} className={`radio-tile ${f.agrees_to_tc === v ? "radio-tile-active" : ""}`}>
                      <input type="radio" name="agrees_to_tc" value={v} checked={f.agrees_to_tc === v} onChange={() => set("agrees_to_tc", v)} data-testid={`gl-tc-${v}`} />
                      <span>{v === "yes" ? "Yes" : "No"}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className={label}>Anything else you'd like us to know? (optional)</label>
                <textarea rows={3} className={field} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="gl-message" />
              </div>

              <label className="flex items-start gap-3 rounded-md border border-altura-gold/40 bg-altura-gold/5 p-4 cursor-pointer">
                <input type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 h-4 w-4 accent-altura-gold-dark shrink-0" data-testid="gl-consent" required />
                <span className="text-sm text-altura-charcoal/80 leading-relaxed">
                  I understand and agree to the <a href="/tgl-terms" target="_blank" rel="noreferrer" className="underline text-altura-espresso decoration-altura-gold hover:text-altura-gold-dark">TGL Terms &amp; Conditions</a>.
                </span>
              </label>

              <button type="submit" disabled={submitting || !f.consent} className="btn-gold w-full sm:w-auto disabled:opacity-60" data-testid="gl-submit">
                {submitting ? "Sending…" : "Submit Application"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
